import PropTypes from 'prop-types';
import React, { Component } from 'react';
import AriaModal from 'react-aria-modal';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ActionCreators as UndoActions } from 'redux-undo';
import * as htmlToImage from 'html-to-image';
import { withRouter } from 'react-router';
import { withT } from '~app/core/BundleProvider';
import { registerHotkey, unregisterHotkey } from '~app/core/useGlobalHotkey';
import ZoomToolUI from '~app/features/app/components/ZoomToolUI';
import CheckButton from '~app/features/app/components/CheckButton';
import ToggleHints from '~app/features/app/components/ToggleHints';
import ZoomButton from '~app/features/app/components/ZoomButton';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as DocumentActions } from '~app/features/document';
import { actions as SaveLoadActions } from '~app/features/saveLoad';
import IconButton from '~app/features/app/components/IconButton';
import withEnterToClick from '~app/core/withEnterToClick';
import FileExists from '~app/features/saveLoad/components/FileExists';
import FileSave from '~app/features/saveLoad/components/FileSave';
import FileSubmit from '~app/features/saveLoad/components/FileSubmit';
import KeyboardShortcutsButton from '~app/features/app/components/KeyboardShortcutsButton';
import { CANVAS_SIZE } from '../../canvas/consts';
import lodash from 'lodash';

import FileMenu from './FileMenu';
import MenuItem from './MenuItem';

//import Logging from '~app/core/logging';
//const log = Logging.getLogger('app.components.menubar');

const ClickOrEnterDiv = withEnterToClick('div');

// on workspace where undo, redo, delete, zoom, re-center button, keyboard shortcut buttons are.
// file save modals also present here
class MenuBar extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    saveToLocal: PropTypes.func.isRequired,
    saveToDrive: PropTypes.func.isRequired,
    exportToLocal: PropTypes.func.isRequired,
    undo: PropTypes.func.isRequired,
    redo: PropTypes.func.isRequired,
    canUndo: PropTypes.bool.isRequired,
    canRedo: PropTypes.bool.isRequired,
    selection: PropTypes.object.isRequired,
    deleteNode: PropTypes.func.isRequired,
    deleteSignal: PropTypes.func.isRequired,
    zoomToDefault: PropTypes.func.isRequired,
    closeModel: PropTypes.func.isRequired,
    isModified: PropTypes.bool.isRequired,
    compareWithSolution: PropTypes.bool.isRequired,
    exitCanvas: PropTypes.func.isRequired,
    mode: PropTypes.string,
    hasSolution: PropTypes.bool.isRequired,
    setName: PropTypes.func.isRequired,
  };

  state = {
    modelSubmitted: false,
    modelSave: false,
    showHamburger: false,
    undoHotkeyId: null,
    redoHotkeyId: null,
    deleteHotkeyId: null,
    showSaveDialogType: '',
    showSaveOptionsDialog: false,
    fileExtension: '.model',
  };

  componentDidMount() {
    const undoHotkeyId = registerHotkey({
      hotkey: 'ctrl+shift+z',
      callback: () => {
        this.handleUndoRedoClick('undo');
      },
      event: 'keydown',
    });
    const redoHotkeyId = registerHotkey({
      hotkey: 'ctrl+shift+y',
      callback: () => {
        this.handleUndoRedoClick('redo');
      },
      event: 'keydown',
    });
    const deleteHotkeyId = registerHotkey({
      hotkeys: ['backspace', 'del'],
      callback: () => {
        this.deleteItem();
      },
      event: 'keydown',
    });
    this.setState({ undoHotkeyId, redoHotkeyId, deleteHotkeyId });
  }

  handleUndoRedoClick = action => {
    if (this.props.compareWithSolution) {
      this.props.exitComparisonState();
    }
    if (action === 'undo') {
      this.props.undo();
    } else {
      this.props.redo();
    }
  };

  componentWillUnmount() {
    unregisterHotkey(this.state.undoHotkeyId);
    unregisterHotkey(this.state.redoHotkeyId);
    unregisterHotkey(this.state.deleteHotkeyId);
  }

  hideFileSaveDialog = () => {
    this.setState({ showSaveDialogType: '' });
    this.setState({ showSaveOptionsDialog: false });
    this.setState({ modelSave: false });
  };

  determineFileExtension() {
    this.props.mode === 'creation'
      ? this.setState({ fileExtension: '_reference.model' })
      : this.props.mode === 'reference' && this.props.hasSolution
      ? this.setState({ fileExtension: '_self-check.model' })
      : this.props.mode === 'reference' && !this.props.hasSolution
      ? this.setState({ fileExtension: '_assessment.model' })
      : this.setState({ fileExtension: '.model' });
  }

  handleFileMenuSelect = info => {
    switch (info) {
      case 'save-options':
        if (this.props.mode === 'creation') {
          this.setState({ showSaveOptionsDialog: true });
        } else {
          this.determineFileExtension();
          this.setState({ showSaveDialogType: 'local' });
          if (this.props.editMode !== 'solution' && !this.props.hasSolution) {
            this.onSubmit();
          } else {
            this.setState({ modelSave: true });
          }
        }
        break;
      case 'save-to-pc':
        this.determineFileExtension();
        this.setState({ showSaveDialogType: 'local' });
        this.setState({ showSaveOptionsDialog: false });
        this.setState({ modelSave: true });
        break;
      case 'export-model':
        this.determineFileExtension();
        this.setState({ showSaveDialogType: 'zip' });
        this.setState({ showSaveOptionsDialog: false });
        this.setState({ modelSave: true });
        break;
      default:
        return;
    }
    this.closeBurgerMenu();
  };

  canDelete = () =>
    (this.props.selection.type === 'node' ||
      this.props.selection.type === 'signal') &&
    !this.props.selection.latched;

  deleteItem = () => {
    if (this.canDelete()) {
      const selection = this.props.selection;
      if (selection.type === 'node') {
        this.props.deleteNode({ label: selection.label });
      } else {
        const [fromLabel, toLabel] = selection.label.split('>');
        this.props.deleteSignal({ fromLabel, toLabel });
      }
    }
  };

  handleFileSave = fileName => {
    const { showSaveDialogType } = this.state;
    const { saveToDrive, saveToLocal, fileInfo } = this.props;

    this.hideFileSaveDialog();

    if (showSaveDialogType === 'drive') {
      // If the name given is the same as when it was loaded and it was loaded
      // from gdrive, then don't prompt to overwrite.
      const overwrite =
        fileInfo?.name === fileName && fileInfo.source === 'drive';
      saveToDrive({ fileName, overwrite });
    } else if (showSaveDialogType === 'local') saveToLocal({ fileName });
    else if (showSaveDialogType === 'image') {
      let totalWorkingModelErrors = -1;
      if (this.props.compareWithSolution && this.props?.workingModelErrors) {
        const {
          objectMistakeCount,
          missingObjectsCount,
          flowMistakeCount,
          connectorMistakeCount,
          missingConnectorCount,
        } = this.props.workingModelErrors;

        totalWorkingModelErrors =
          objectMistakeCount +
          missingObjectsCount +
          flowMistakeCount +
          connectorMistakeCount +
          missingConnectorCount;
      }

      const pngTopBorder =
        totalWorkingModelErrors < 0
          ? '0 none'
          : totalWorkingModelErrors === 0
          ? '20px solid #9edd87'
          : '20px solid #a94442';
      this.props.zoomToDefault();
      document.activeElement.blur();
      this.props.selectItem({});
      htmlToImage
        .toBlob(document.getElementById('capture'), {
          backgroundColor: 'white',
          width: CANVAS_SIZE,
          height: CANVAS_SIZE,
          cacheBust: true,
          style: { borderTop: pngTopBorder },
          pixelRatio: 1,
        })
        .then(blob => {
          window.saveAs(blob, fileName);
        });
    } else if (showSaveDialogType === 'zip') {
      this.props.setName(fileName);
      this.props.exportToLocal({ baseName: fileName });
    }
  };

  handleFileSubmit = fileName => {
    const { saveToLocal } = this.props;
    this.hideFileSaveDialog();
    this.resetSubmit();
    this.props.setSavedDateTime();
    saveToLocal({ fileName });
  };

  openBurgerMenu = () => this.setState({ showHamburger: true });
  closeBurgerMenu = () => this.setState({ showHamburger: false });
  resetModel = () => {
    this.props.resetModel();
  };

  onSubmit = () => this.setState({ modelSubmitted: true });
  resetSubmit = () => this.setState({ modelSubmitted: false });

  render() {
    const { t, exitCanvas } = this.props;

    return (
      <div>
        {this.state.showHamburger ? (
          <div>
            <FileMenu
              closeMenu={this.closeBurgerMenu}
              handleSelect={this.handleFileMenuSelect}
              exitCanvas={exitCanvas}
              resetModel={this.resetModel}
            />
          </div>
        ) : (
          <div className="MenuBar">
            <ZoomToolUI />
            <ZoomButton />
            <KeyboardShortcutsButton />
            <div>
              <MenuItem
                title={t('delete-shortcut')}
                styleName="trash"
                ariaLabel={t('delete-shortcut')}
                tabIndex="0"
                onClick={this.deleteItem}
                disabled={!this.canDelete()}
              />
            </div>
            <MenuItem
              title={t('undo-shortcut')}
              styleName="undo"
              ariaLabel={t('undo-shortcut')}
              tabIndex="0"
              onClick={() => this.handleUndoRedoClick('undo')}
              disabled={!this.props.canUndo}
            />
            <MenuItem
              title={t('redo-shortcut')}
              styleName="redo"
              ariaLabel={t('redo-shortcut')}
              tabIndex="0"
              onClick={() => this.handleUndoRedoClick('redo')}
              disabled={!this.props.canRedo}
            />
            <ToggleHints />
            <CheckButton />
            <MenuItem
              styleName="hamburger"
              tabIndex="0"
              ariaLabel={t('open-file-menu')}
              onClick={this.openBurgerMenu}
            />
          </div>
        )}
        <AriaModal
          mounted={this.state.showSaveOptionsDialog}
          onExit={this.hideFileSaveDialog}
          titleText={t('save-type')}
        >
          <div className="Modal__container">
            <div className="ButtonBox__box">
              <div class="ButtonBox__CreationToolSave">
                <div className="Modal__exit-container">
                  <div className="Modal__icon">
                    <IconButton
                      styleName="Modal__exit"
                      onClick={this.hideFileSaveDialog}
                      ariaLabel={t('close-menu')}
                      tabIndex="0"
                    />
                  </div>
                </div>
                {t('save-type')}
                <div className={'ButtonBox__description'}>
                  {t('select-save-type')}
                </div>
                <div className="row">
                  <div className="col">
                    <div className="ButtonBox__inner-container">
                      <div className={'ButtonBox__description'}>
                        {t('save-to-pc-description')}
                      </div>
                      <div className="ButtonBox__button-container-fixed">
                        <ClickOrEnterDiv
                          className="ButtonBox__button"
                          onClick={() =>
                            this.handleFileMenuSelect('save-to-pc')
                          }
                          role="button"
                          aria-label="save as model file"
                          tabIndex="0"
                        >
                          {t('save-model')}
                        </ClickOrEnterDiv>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="ButtonBox__inner-container">
                      <div className={'ButtonBox__description'}>
                        {t('export-to-pc-description')}
                      </div>
                      <div className="ButtonBox__button-container-fixed">
                        <ClickOrEnterDiv
                          className="ButtonBox__button"
                          onClick={() =>
                            this.handleFileMenuSelect('export-model')
                          }
                          role="button"
                          aria-label="save as ZIP file"
                          tabIndex="0"
                        >
                          {t('save-zip')}
                        </ClickOrEnterDiv>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AriaModal>
        <AriaModal
          mounted={this.state.modelSubmitted}
          onExit={this.hideFileSaveDialog && this.resetSubmit}
          titleText={t('submit')}
        >
          <FileSubmit
            type={this.state.showSaveDialogType}
            handleSubmit={this.handleFileSubmit}
            onCancel={this.hideFileSaveDialog && this.resetSubmit}
            extension={this.state.fileExtension}
          />
        </AriaModal>
        <AriaModal
          mounted={this.state.modelSave}
          onExit={this.hideFileSaveDialog}
          titleText={t('save-to-pc')}
        >
          <FileSave
            type={this.state.showSaveDialogType}
            handleSave={this.handleFileSave}
            onCancel={this.hideFileSaveDialog}
            defaultFileName={this.props.fileInfo?.name.substring(
              0,
              this.props.fileInfo?.name.lastIndexOf(this.state.fileExtension),
            )}
            extension={this.state.fileExtension}
          />
        </AriaModal>

        <AriaModal
          mounted={!!this.props.fileExistsPrompt}
          titleText={t('file-exists-title')}
        >
          <FileExists
            onCancel={() => this.setState({ showSaveDialogType: 'drive' })}
          />
        </AriaModal>
      </div>
    );
  }
}

const mapStateToProps = ({ canvas, document, saveLoad }) => ({
  canUndo: document.past.length > 0,
  canRedo: document.future.length > 0,
  selection: canvas.selection,
  isDriveReady: saveLoad.google.isApiLoaded,
  fileInfo: canvas.fileInfo,
  fileExistsPrompt: saveLoad.fileExistsPrompt,
  isModified: !!document.present?.isModified,
  compareWithSolution: canvas.compareWithSolution,
  workingModelErrors: document.present?.workingModelErrors,
  mode: document.present?.mode,
  hasSolution: !lodash.isEmpty(document.present?.solutionModel?.nodes),
  editMode: document.present?.editMode,
});

const mapDispatchToProps = {
  saveToLocal: SaveLoadActions.saveToLocal,
  undo: UndoActions.undo,
  redo: UndoActions.redo,
  deleteNode: DocumentActions.deleteNode,
  deleteSignal: DocumentActions.deleteSignal,
  setSavedDateTime: DocumentActions.setSavedDateTime,
  saveToDrive: SaveLoadActions.saveToDrive,
  exportToLocal: SaveLoadActions.exportToLocal,
  zoomToDefault: CanvasActions.zoomToDefault,
  closeModel: DocumentActions.closeModel,
  resetModel: DocumentActions.resetModel,
  selectItem: CanvasActions.selectItem,
  setName: DocumentActions.setName,
  exitComparisonState: CanvasActions.exitComparisonState,
};

export default compose(
  withRouter,
  withT,
  connect(mapStateToProps, mapDispatchToProps),
)(MenuBar);
